import React from 'react';
import { clsx } from 'clsx';
import { useLogger } from '@/lib/logger';
import {
  AngleDownIcon,
  AngleRightIcon,
  ArrowDownRightIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpCircleIcon,
  ArrowUpLeftIcon,
  CalculatorIcon,
  ChatsTextIcon,
  CheckCircleOutlineIcon,
  CheckCircleIcon,
  CheckIcon,
  ClockFillIcon,
  ClockIcon,
  CoinsIcon,
  CreditCardIcon,
  DownloadAltIcon,
  ExclamationCircleOutlineIcon,
  ExclamationCircleIcon,
  FicoIcon,
  FileDownloadIcon,
  FileLockIcon,
  FileUploadIcon,
  HelpCircleIcon,
  InfoCircleIcon,
  LoaderIcon,
  LockIcon,
  LogOutIcon,
  MenuIcon,
  MessageCircleIcon,
  MinusIcon,
  MobileIcon,
  MoneyBillsIcon,
  PenIcon,
  PercentIcon,
  PhoneIcon,
  PlusIcon,
  QuestionCircleIcon,
  RefreshIcon,
  RotateLeftIcon,
  RotateIcon,
  ShieldCheckFillIcon,
  ShieldCheckIcon,
  ShieldIcon,
  ShoppingCartIcon,
  TimeForwardIcon,
  TimesCircleIcon,
  TimesIcon,
  TrendUpIcon,
  VectorUpIcon,
  type IconName,
} from '../../assets/icons/index';

// Icon mapping for dynamic imports
const iconMap: Record<
  IconName,
  React.FC<React.SVGProps<SVGSVGElement> & { title?: string }>
> = {
  'angle-down': AngleDownIcon,
  'angle-right': AngleRightIcon,
  'arrow-down-right': ArrowDownRightIcon,
  'arrow-left': ArrowLeftIcon,
  'arrow-right': ArrowRightIcon,
  'arrow-up-circle': ArrowUpCircleIcon,
  'arrow-up-left': ArrowUpLeftIcon,
  'calculator': CalculatorIcon,
  'chats-text': ChatsTextIcon,
  'check-circle-outline': CheckCircleOutlineIcon,
  'check-circle': CheckCircleIcon,
  'check': CheckIcon,
  'clock-fill': ClockFillIcon,
  'clock': ClockIcon,
  'coins': CoinsIcon,
  'credit-card': CreditCardIcon,
  'download-alt': DownloadAltIcon,
  'exclamation-circle-outline': ExclamationCircleOutlineIcon,
  'exclamation-circle': ExclamationCircleIcon,
  'fico': FicoIcon,
  'file-download': FileDownloadIcon,
  'file-lock': FileLockIcon,
  'file-upload': FileUploadIcon,
  'help-circle': HelpCircleIcon,
  'info-circle': InfoCircleIcon,
  'loader': LoaderIcon,
  'lock': LockIcon,
  'log-out': LogOutIcon,
  'menu': MenuIcon,
  'message-circle': MessageCircleIcon,
  'minus': MinusIcon,
  'mobile': MobileIcon,
  'money-bills': MoneyBillsIcon,
  'pen': PenIcon,
  'percent': PercentIcon,
  'phone': PhoneIcon,
  'plus': PlusIcon,
  'question-circle': QuestionCircleIcon,
  'refresh': RefreshIcon,
  'rotate-left': RotateLeftIcon,
  'rotate': RotateIcon,
  'shield-check-fill': ShieldCheckFillIcon,
  'shield-check': ShieldCheckIcon,
  'shield': ShieldIcon,
  'shopping-cart': ShoppingCartIcon,
  'time-forward': TimeForwardIcon,
  'times-circle': TimesCircleIcon,
  'times': TimesIcon,
  'trend-up': TrendUpIcon,
  'vector-up': VectorUpIcon,
};

interface IconProps {
  /** Icon component to render */
  icon?: React.FC<React.SVGProps<SVGSVGElement> & { title?: string }>;
  /** Icon name for dynamic lookup with autocompletion */
  name?: IconName;
  /** Size of the icon (number for pixels or string for any CSS unit) */
  size?: number | string;
  /** CSS class name for styling */
  className?: string;
  /** Additional SVG props */
  [key: string]: unknown;
}

/**
 * Icon component that provides type-safe access to all SVG icons
 *
 * @example
 * // Using icon prop (direct component)
 * <Icon icon={ArrowRightIcon} size={24} className="text-blue-500" />
 *
 * @example
 * // Using name prop (with autocompletion)
 * <Icon name="arrow-right" size="1.5rem" className="text-red-500" />
 */
export const Icon: React.FC<IconProps> = ({
  icon: Svg,
  name,
  size = 20,
  className,
  ...props
}) => {
  const logger = useLogger();
  // Get icon component from either prop or name lookup
  const IconComponent = Svg || (name ? iconMap[name] : null);

  if (!IconComponent) {
    logger.warn(`Icon component not found for name: ${name}`);
    return null;
  }

  return (
    <IconComponent
      width={size}
      height={size}
      className={clsx('inline-block', className)}
      aria-hidden="true"
      {...props}
    />
  );
};

// Export individual icons for direct usage
export {
  AngleDownIcon,
  AngleRightIcon,
  ArrowDownRightIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpCircleIcon,
  ArrowUpLeftIcon,
  CalculatorIcon,
  ChatsTextIcon,
  CheckCircleOutlineIcon,
  CheckCircleIcon,
  CheckIcon,
  ClockFillIcon,
  ClockIcon,
  CoinsIcon,
  CreditCardIcon,
  DownloadAltIcon,
  ExclamationCircleOutlineIcon,
  ExclamationCircleIcon,
  FicoIcon,
  FileDownloadIcon,
  FileLockIcon,
  FileUploadIcon,
  HelpCircleIcon,
  InfoCircleIcon,
  LoaderIcon,
  LockIcon,
  LogOutIcon,
  MenuIcon,
  MessageCircleIcon,
  MinusIcon,
  MobileIcon,
  MoneyBillsIcon,
  PenIcon,
  PercentIcon,
  PhoneIcon,
  PlusIcon,
  QuestionCircleIcon,
  RefreshIcon,
  RotateLeftIcon,
  RotateIcon,
  ShieldCheckFillIcon,
  ShieldCheckIcon,
  ShieldIcon,
  ShoppingCartIcon,
  TimeForwardIcon,
  TimesCircleIcon,
  TimesIcon,
  TrendUpIcon,
  VectorUpIcon,
  type IconName,
} from '../../assets/icons/index';
